import React, { useEffect, useState } from "react";
import CopyToClipboard from "react-copy-to-clipboard";
import Draggable from "react-draggable";
import ReactTooltip from "react-tooltip";
import NumberFormat from "react-number-format";
import Portal from "../../../common/components/Portal";
import ConfirmationModal from "../../../common/components/ConfirmationModal";
import { RichEditorPopup } from "../../../common/components/formFields/RichEditorPopup";
import { EllipsisTooltipText } from "../../../common/components/EllipsisTextWithTooltip";

export const AIValueEditModal = ({
    fieldName,
    label = "",
    value: initialValue = "",
    type = "string", // number | text | memo | string (fallback, textarea)
    rules = {},
    saveLabel = "Save",
    isSaving = false,
    currentValue,
    entityLabel = "Mobile Asset",
    onSave,
    onCancel
}) => {
    const [value, setValue] = useState(initialValue ?? "");
    const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
    const [copied, setCopied] = useState(false);

    const isEmpty = String(value ?? "")?.trim() === "";
    const isChanged = value !== initialValue;
    const canSave = !isEmpty && !isSaving;
    const displayCurrentValue = currentValue === null || currentValue === undefined || currentValue === "" ? "-" : String(currentValue);
    const canCopyCurrent = displayCurrentValue !== "-";

    useEffect(() => {
        ReactTooltip.rebuild();
    }, [isEmpty, isChanged, copied]);

    useEffect(() => {
        if (!copied) return;
        const timer = setTimeout(() => setCopied(false), 2000);
        return () => clearTimeout(timer);
    }, [copied]);

    const handleCancel = () => {
        if (value !== initialValue) {
            setShowCancelConfirmation(true);
            return;
        }
        onCancel();
    };

    const handleSave = newValue => {
        if (typeof onSave === "function") onSave({ [fieldName]: newValue });
    };

    if (type === "memo") {
        return (
            <Portal
                body={
                    <RichEditorPopup
                        value={initialValue}
                        label={label}
                        editorKey={`ai-edit-${fieldName}`}
                        saveLabel={saveLabel}
                        saveData={handleSave}
                        onCancel={onCancel}
                    />
                }
                onCancel={onCancel}
            />
        );
    }

    return (
        <>
            <Portal
                body={
                    <div
                        className="modal analyse_reference_modal ai-edit-value-modal"
                        style={{ display: "block" }}
                        tabIndex="-1"
                        role="dialog"
                        aria-hidden="true"
                    >
                        <div className="modal-dialog" role="document">
                            <Draggable handle=".draggable" cancel=".not-draggable">
                                <div className="modal-content">
                                    <div className="modal-header draggable">
                                        <h5 className="modal-title">Edit AI Analyzed Value</h5>
                                        <button type="button" className="close" onClick={handleCancel}>
                                            <span aria-hidden="true">×</span>
                                        </button>
                                    </div>
                                    <div className="modal-body analyse_reference_body not-draggable">
                                        <div className="ai-edit-field-title">{label}</div>

                                        <div className="ai-edit-section mb-4">
                                            <div className="ai-edit-field-header">
                                                <div className="ai-edit-section-label">AI Value</div>
                                                {isChanged && (
                                                    <span className="ai-edit-reset cursor-hand" onClick={() => setValue(initialValue ?? "")}>
                                                        <i className="fas fa-undo mr-1" />
                                                        Reset
                                                    </span>
                                                )}
                                            </div>
                                            {type === "number" ? (
                                                <NumberFormat
                                                    autoFocus
                                                    autoComplete="off"
                                                    className="form-control"
                                                    placeholder={label}
                                                    value={value ?? ""}
                                                    thousandSeparator={rules?.thousand_separator}
                                                    prefix={rules?.prefix}
                                                    suffix={rules?.suffix}
                                                    decimalScale={rules?.decimal_scale}
                                                    allowNegative={rules?.allow_negative}
                                                    displayType="input"
                                                    onValueChange={values => setValue(values?.value ?? "")}
                                                />
                                            ) : (
                                                <input
                                                    type="text"
                                                    autoFocus
                                                    autoComplete="off"
                                                    className="form-control"
                                                    placeholder={label}
                                                    value={value ?? ""}
                                                    onChange={e => setValue(e.target.value)}
                                                />
                                            )}
                                        </div>

                                        <div className="ai-edit-section">
                                            <div className="ai-edit-field-header">
                                                <div className="ai-edit-section-label">Current {entityLabel} Value</div>
                                                {canCopyCurrent ? (
                                                    <CopyToClipboard text={displayCurrentValue} onCopy={() => setCopied(true)}>
                                                        <span
                                                            className="ai-edit-copy cursor-hand"
                                                            data-tip={copied ? "Copied" : "Copy"}
                                                            data-for="asset-info-tooltips"
                                                            data-place="left"
                                                        >
                                                            {copied ? (
                                                                <i className="fa fa-check" style={{ color: "green" }} />
                                                            ) : (
                                                                <i className="fa fa-copy" style={{ color: "#4B90FA" }} />
                                                            )}
                                                        </span>
                                                    </CopyToClipboard>
                                                ) : (
                                                    <span
                                                        className="ai-edit-copy"
                                                        style={{ cursor: "not-allowed" }}
                                                        data-tip="No value to copy"
                                                        data-for="asset-info-tooltips"
                                                        data-place="left"
                                                    >
                                                        <i className="fa fa-copy" style={{ color: "#4B90FA", opacity: 0.5 }} />
                                                    </span>
                                                )}
                                            </div>
                                            <div className="ai-edit-readonly">
                                                <EllipsisTooltipText
                                                    className="ai-edit-readonly-text"
                                                    text={displayCurrentValue}
                                                    tooltipId="asset-info-tooltips"
                                                    tooltipPlace="bottom"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="modal-footer">
                                        <button type="button" className="btn btn-secondary btnClr footer-button" onClick={handleCancel}>
                                            Cancel
                                        </button>
                                        <span
                                            className="d-inline-block ml-2"
                                            data-tip={isEmpty ? "Enter a value to save" : saveLabel}
                                            data-for="asset-info-tooltips"
                                            data-place="top"
                                        >
                                            <button
                                                type="button"
                                                className={`btn btn-primary btnRgion footer-button ${canSave ? "" : "cursor-disabled"}`}
                                                disabled={!canSave}
                                                onClick={() => {
                                                    if (canSave) handleSave(value);
                                                }}
                                            >
                                                {isSaving ? (
                                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                ) : (
                                                    "Save"
                                                )}
                                            </button>
                                        </span>
                                    </div>
                                </div>
                            </Draggable>
                        </div>
                    </div>
                }
                onCancel={handleCancel}
            />
            {showCancelConfirmation && (
                <Portal
                    body={
                        <ConfirmationModal
                            type="cancel"
                            heading={"You have unsaved changes!"}
                            message={"This action cannot be reverted, are you sure you want to leave?"}
                            onNo={() => setShowCancelConfirmation(false)}
                            onYes={onCancel}
                        />
                    }
                    onCancel={() => setShowCancelConfirmation(false)}
                />
            )}
        </>
    );
};

export default AIValueEditModal;
